/**
 * The frame both administration applications use, from `design/new design /legacy/admin_*`.
 *
 * Shared because there are two of them now — the Agents Assistant's back office and the Customer
 * Bot Dashboard — and an administrator who opens both should not have to learn two layouts. It
 * moved here from `back-office/src/components/` when the dashboard was separated; a copy in each
 * application would have been two shells that drift.
 *
 * The same shell as the assistant: a 260px rail that is `hidden lg:flex` and becomes a drawer
 * below it, a main column, and a right-hand panel that is a slide-over on a phone and a third
 * column on a desktop. The design's own source uses the identical markup for both applications,
 * so this uses the identical `SideRail` — see that file for why the chrome is shared.
 *
 * ## The header is two rows, and that is a real decision rather than a layout
 *
 * The design puts the page title and its primary action on the first row, and the filters and
 * search on the second. That separation is what keeps "Upload Document" in the same place on every
 * screen while the filters underneath change completely between the knowledge base and the
 * supervision queue — an administrator learns one position for the action and reads the row below
 * it for context.
 *
 * Below `lg` both rows collapse into a single bar with the drawer toggles, because a phone cannot
 * show five filter chips and a search field without becoming a form.
 */

import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { MenuIcon } from "../ui/icons";
import { cx } from "../ui/cx";
import { SideRail } from "./SideRail";

export interface AdminShellProps {
  /** The page's name, on the first header row. */
  title: string;
  /** The navigation, rendered inside the rail. */
  nav: ReactNode;
  /** The primary action, top right — "Upload Document" and its equivalents. */
  action?: ReactNode;
  /** Filters and search, on the second header row. */
  toolbar?: ReactNode;
  /** The right-hand panel, when the screen has one. */
  panel?: ReactNode;
  /** Whether that panel is open. Below `lg` it is a drawer; above, a column. */
  panelOpen?: boolean;
  onPanelClose?: () => void;
  onPanelOpen?: () => void;
  /** What the panel toggle is called, for the button's accessible name. */
  panelLabel?: string;
  children: ReactNode;
}

export function AdminShell({
  title,
  nav,
  action,
  toolbar,
  panel,
  panelOpen = false,
  onPanelClose,
  onPanelOpen,
  panelLabel,
  children,
}: AdminShellProps): JSX.Element {
  const { t } = useTranslation("app");
  const [railOpen, setRailOpen] = useState(false);

  // Escape closes whichever drawer is open. Only bound while one is, so it does not swallow the
  // key from a dialog on the page underneath.
  useEffect(() => {
    if (!railOpen && !panelOpen) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if (railOpen) setRailOpen(false);
      else onPanelClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [railOpen, panelOpen, onPanelClose]);

  return (
    <div className="flex h-screen min-h-0 w-full overflow-hidden bg-surfaceRaised">
      <SideRail className="hidden lg:flex">{nav}</SideRail>

      {/* The same rail as a drawer, below `lg`. One component, so the two cannot drift. */}
      <div
        aria-hidden="true"
        onClick={() => setRailOpen(false)}
        className={cx(
          "fixed inset-0 z-[90] bg-surfaceInverse/50 backdrop-blur-sm transition-opacity lg:hidden",
          railOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <div
        className={cx(
          "fixed inset-y-0 start-0 z-[91] transition-transform duration-300 lg:hidden",
          railOpen ? "translate-x-0" : "-translate-x-full rtl:translate-x-full",
        )}
      >
        <SideRail className="shadow-2xl">
          <div onClick={() => setRailOpen(false)}>{nav}</div>
        </SideRail>
      </div>

      <main className="relative flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-surfaceRaised">
        <header className="z-10 hidden shrink-0 flex-col gap-4 border-b border-borderSubtle bg-surfaceRaised px-6 py-5 lg:flex">
          <div className="flex items-center justify-between gap-4">
            <h1 className="truncate text-heading-md font-bold tracking-tight text-primary">
              <bdi>{title}</bdi>
            </h1>
            <div className="flex shrink-0 items-center gap-3">
              {panel && !panelOpen && onPanelOpen ? (
                <button
                  type="button"
                  onClick={onPanelOpen}
                  className="flex h-9 items-center gap-2 rounded-xl border border-borderStrong bg-surfaceRaised px-3 text-body-sm font-semibold text-primary transition-all hover:bg-primarySubtle"
                >
                  {panelLabel}
                </button>
              ) : null}
              {action}
            </div>
          </div>
          {toolbar ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              {toolbar}
            </div>
          ) : null}
        </header>

        {/* One bar below `lg`: the rail toggle, the title, the panel toggle. */}
        <div className="z-10 flex w-full shrink-0 items-center justify-between gap-2 border-b border-borderStrong bg-surfaceRaised p-3 shadow-sm md:p-4 lg:hidden">
          <button
            type="button"
            onClick={() => setRailOpen(true)}
            title={t("shell.menu")}
            aria-label={t("shell.menu")}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-borderStrong bg-surfaceRaised text-primary shadow-sm"
          >
            <MenuIcon />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-body-md font-bold tracking-tight text-primary">
            <bdi>{title}</bdi>
          </h1>
          <div className="flex shrink-0 items-center gap-2">{action}</div>
        </div>

        {/* The content region, and the only thing on this screen that scrolls.
            
            `overflow-y-auto`, not `overflow-hidden`. This was hidden, which is correct for a page
            that manages its own scrolling — Supervision has an `overflow-auto` table body — and
            silently destructive for one that does not: an ordinary content column simply ran past
            the bottom of the box and was unreachable. Measured at 1440x800: 6191px of Features,
            4332px of Actions and 1619px of Analytics had no way to be seen, with no scrollbar to
            suggest anything was missing.
            
            A page with its own inner scroller is unaffected: it is still `min-h-0 flex-1` inside
            this flex column, so it sizes to this box and this box never overflows.

            `relative` so this is the containing block for what it holds. Without it an absolutely
            positioned descendant resolves against `main` instead and does not scroll with the
            content — measured, the `sr-only` captions on every data table were pinned outside this
            box. Invisible, so nothing looked wrong; it inflated the height of a region that is
            supposed to be exactly the viewport. */}
        <div
          data-testid="admin-content"
          className="relative flex min-h-0 flex-1 flex-col overflow-y-auto bg-surface p-4 lg:bg-surfaceRaised lg:p-6"
        >
          {/* The toolbar again, below `lg`, where the header could not hold it. */}
          {toolbar ? (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-borderStrong bg-surfaceRaised p-3 md:p-4 lg:hidden">
              {toolbar}
            </div>
          ) : null}
          {children}
        </div>
      </main>

      {panel ? (
        <>
          <div
            aria-hidden="true"
            onClick={onPanelClose}
            className={cx(
              "fixed inset-0 z-[100] bg-surfaceInverse/50 backdrop-blur-sm transition-opacity lg:hidden",
              panelOpen ? "opacity-100" : "pointer-events-none opacity-0",
            )}
          />
          <aside
            className={cx(
              "fixed inset-y-0 end-0 z-[101] flex h-full w-[85vw] max-w-[400px] flex-col border-s border-borderSubtle bg-surfaceSunken shadow-2xl transition-transform duration-300",
              "lg:relative lg:z-20 lg:w-[380px] lg:translate-x-0 lg:shadow-none",
              // Hidden entirely at `lg` when closed, rather than translated: a 380px column that
              // is merely off-screen still takes its width out of the layout.
              panelOpen
                ? "translate-x-0"
                : "translate-x-full rtl:-translate-x-full lg:hidden",
            )}
          >
            {panel}
          </aside>
        </>
      ) : null}
    </div>
  );
}
